import {
  Button,
  Checkbox,
  Container,
  Form,
  Input,
  Message,
  Stack,
  Text,
} from "basis";
import { CancelLink } from "components/IncreaseCreditLimit/components/CancelLink";
import { Separator } from "components/Separator/Separator";
import { postHref } from "library/api";
import { deleteHref } from "library/api/httpMethods";
import { formatAmount } from "library/moneyUtils";
import { useState } from "react";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import { Cancel } from "./Cancel";
import {
  AcknowledgeApplicationResponse,
  ActionType,
  ApplicationStatus,
  FinalCancelCliResponse,
  ScreenProps,
} from "./types";
import {
  getMinMonthlyPayment,
  parseAcknowledgeApplicationResponse,
} from "./utils";
import { validateNewCreditLimit } from "./validation";

type Values = {
  newCreditLimit: string;
  iConfirm: boolean;
};

export const ConfirmMmp = ({ state, dispatch }: ScreenProps) => {
  const [showModal, setShowModal] = useState(false);
  const { accountInfo, isConditionallyApproved } = state;
  const acknowledgementUrl = state.acknowledgementUrl as string;
  const initialValues: Values = {
    newCreditLimit: state.newCreditLimit,
    iConfirm: false,
  };
  const requestedCreditLimit = state.requestedCreditLimit as number;

  const finalCancelMutation = useMutation((url: string) =>
    deleteHref<FinalCancelCliResponse>(url),
  );

  const acknowledgementMutation = useMutation(async (values: Values) => {
    const response = await postHref<AcknowledgeApplicationResponse>(
      acknowledgementUrl,
      {
        accepted_credit_limit: Number(values.newCreditLimit) * 100,
      },
    );
    const responseData = parseAcknowledgeApplicationResponse(response.data);

    if (
      responseData === null ||
      responseData.application.status === "INTERNAL_SERVICE_ERROR"
    ) {
      throw new window.Error(
        "Invalid acknowledge CLI application response received",
      );
    }

    return responseData;
  });
  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    acknowledgementMutation.mutate(values, {
      onSuccess: (responseData) => {
        const { application, bankConnectUrl, latitudePoiUrl, cancelUrl } =
          responseData;

        if (application.status === ApplicationStatus.CLI_PENDING_POI) {
          dispatch({
            type: ActionType.CONFIRM_MMP_POI_REQUESTED,
            bankConnectUrl,
            latitudePoiUrl,
            cancelUrl: cancelUrl as string,
          });
        } else {
          dispatch({
            type: ActionType.CONFIRM_MMP_SUCCEEDED,
            minimumMonthlyPayment: getMinMonthlyPayment({
              creditLimit: values.newCreditLimit,
              minMonthlyPaymentPercentage: accountInfo.cardMmpPercentage,
              minMonthlyPaymentThreshold: accountInfo.cardMmpThreshold,
            }),
            approvedCreditLimit: values.newCreditLimit,
          });
        }
      },
      onError: () => {
        dispatch({
          type: ActionType.ACKNOWLEDGE_APPLICATION_FAILED,
        });
      },
    });
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values, errors } }) => {
        const isNewCreditLimitValid =
          /^\d+$/.test(values.newCreditLimit) &&
          errors.newCreditLimit === undefined;

        return (
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo.cardName}
                cardEndsWith={accountInfo.cardEndsWith}
              />
            </Container>
            <Text as="h3" textStyle="heading4">
              {isConditionallyApproved
                ? "You've been conditionally approved"
                : "You're approved!"}
            </Text>
            <Separator />
            {requestedCreditLimit > Number(state.newCreditLimit) && (
              <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
                You've been approved for a lower limit than the one that you
                requested. If you would still like to increase your credit limit
                please confirm below.
              </Message>
            )}
            <Container width="280">
              <Input
                name="newCreditLimit"
                label="Confirm your new credit limit"
                maxLength="5"
                variant="numeric"
                prefix="$"
                helpText="Confirm your requested limit or enter a lower limit. Round up to the nearest $100."
                validate={validateNewCreditLimit}
                validateData={{
                  creditLimit: accountInfo.creditLimit,
                  maxCreditLimit: accountInfo.cardMaxCreditLimit,
                  newCreditLimit: Number(state.newCreditLimit),
                }}
              />
            </Container>
            <Text testId="minimum-monthly-payment">
              <strong>
                Minimum monthly payment:{" "}
                {isNewCreditLimitValid
                  ? formatAmount(
                      getMinMonthlyPayment({
                        creditLimit: values.newCreditLimit,
                        minMonthlyPaymentPercentage:
                          accountInfo.cardMmpPercentage,
                        minMonthlyPaymentThreshold:
                          accountInfo.cardMmpThreshold,
                      }),
                    )
                  : "–"}
              </strong>
            </Text>
            <Separator />
            <Text>
              Your minimum monthly payment is calculated based on your interest
              rate and balance reaching the requested credit limit. It is not
              inclusive of promotional purchases, fees and charges.
            </Text>
            <Checkbox
              name="iConfirm"
              label="Confirm that you can make these payments without financial hardship."
              hideLabel
            >
              I confirm that, based on my current and foreseeable future
              financial circumstances, I will be able to make these payments
              without financial hardship.
            </Checkbox>
            {isConditionallyApproved && (
              <Text>
                New credit limit is subject to you providing proof of income.
              </Text>
            )}
            <Button
              type="submit"
              width="240"
              loading={acknowledgementMutation.isLoading}
            >
              Confirm new limit
            </Button>
            <CancelLink onClick={() => setShowModal(true)} />
            <Cancel
              loading={finalCancelMutation.isLoading}
              isOpen={showModal}
              onClose={() => {
                setShowModal(false);
              }}
              onConfirm={() => {
                finalCancelMutation.mutate(state.cancelUrl as string, {
                  onSuccess() {
                    dispatch({ type: ActionType.FINAL_CANCEL });
                  },
                  onError() {
                    dispatch({ type: ActionType.FINAL_CANCEL_FAILED });
                  },
                });
              }}
            />
          </Stack>
        );
      }}
    </Form>
  );
};
